import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';
import type { AuditAction } from '../../types';
import Pagination from './Pagination';

interface Filters {
  action: string;
  user: string;
  admin: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: Filters = { action: '', user: '', admin: '', dateFrom: '', dateTo: '' };

function useDebouncedValue(value: string, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const AuditLogTab: React.FC = () => {
  const { t } = useTranslation();

  const ACTION_LABELS: Record<AuditAction, { label: string; color: string }> = useMemo(() => ({
    EMAIL_VERIFIED:      { label: t('admin.actionEmailVerified'),  color: 'bg-blue-100 text-blue-700'     },
    APPROVED:            { label: t('admin.actionApproved'),       color: 'bg-green-100 text-green-700'   },
    REJECTED:            { label: t('admin.actionRejected'),       color: 'bg-red-100 text-red-700'       },
    REAPPLIED:           { label: t('admin.actionReapplied'),      color: 'bg-yellow-100 text-yellow-700' },
    BOOKING_CREATED:     { label: t('admin.actionBooked'),         color: 'bg-emerald-100 text-emerald-700' },
    BOOKING_CANCELLED:   { label: t('admin.actionCancelled'),      color: 'bg-orange-100 text-orange-700'   },
    WAITLIST_JOINED:     { label: t('admin.actionJoinedWaitlist'), color: 'bg-amber-100 text-amber-700'     },
    WAITLIST_LEFT:       { label: t('admin.actionLeftWaitlist'),    color: 'bg-amber-100 text-amber-700'     },
    WAITLIST_PROMOTED:   { label: t('admin.actionAutoPromoted'),   color: 'bg-indigo-100 text-indigo-700'   },
    TOKEN_INVALID_USAGE: { label: t('admin.actionInvalidToken'),   color: 'bg-rose-100 text-rose-700'       },
    EVENT_DELETED:       { label: t('admin.actionEventDeleted'),   color: 'bg-purple-100 text-purple-700'   },
  }), [t]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [userInput, setUserInput] = useState('');
  const [adminInput, setAdminInput] = useState('');

  const debouncedUser = useDebouncedValue(userInput);
  const debouncedAdmin = useDebouncedValue(adminInput);

  // Sync debounced values into filters and reset page
  useEffect(() => {
    setFilters((f) => {
      if (f.user === debouncedUser) return f;
      return { ...f, user: debouncedUser };
    });
    setPage(1);
  }, [debouncedUser]);

  useEffect(() => {
    setFilters((f) => {
      if (f.admin === debouncedAdmin) return f;
      return { ...f, admin: debouncedAdmin };
    });
    setPage(1);
  }, [debouncedAdmin]);

  const updateFilter = useCallback((key: keyof Filters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setUserInput('');
    setAdminInput('');
    setPage(1);
  }, []);

  const hasFilters = Object.values(filters).some(Boolean);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', page, pageSize, filters],
    queryFn: () => adminAPI.getAuditLog(page, filters, pageSize),
    placeholderData: (prev: any) => prev,
  });

  if (isLoading && !data) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }

  const { entries = [], total = 0, pageSize: serverPageSize = 25 } = data ?? {};
  const effectivePageSize = serverPageSize || pageSize;
  const totalPages = Math.ceil(total / effectivePageSize);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{total} total entr{total !== 1 ? 'ies' : 'y'}</p>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{t('admin.dateFrom')}</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter('dateFrom', e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{t('admin.dateTo')}</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => updateFilter('dateTo', e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{t('admin.action')}</label>
          <select
            value={filters.action}
            onChange={(e) => updateFilter('action', e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">{t('admin.allActions')}</option>
            {Object.entries(ACTION_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{t('admin.user')}</label>
          <input
            type="text"
            placeholder={t('admin.searchUser')}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{t('admin.admin_role')}</label>
          <input
            type="text"
            placeholder={t('admin.searchAdmin')}
            value={adminInput}
            onChange={(e) => setAdminInput(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-44"
          />
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
          >
            {t('admin.clearFilters')}
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{hasFilters ? t('admin.noMatchingEntries') : t('admin.noAuditEntries')}</p>
        </div>
      ) : (
        <>
          {/* Desktop: Table */}
          <div className="hidden lg:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">{t('admin.date')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.action')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.user')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.admin_role')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.note')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {entries.map((entry) => {
                  const badge = ACTION_LABELS[entry.action] ?? { label: entry.action, color: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={entry.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500">
                        {format(new Date(entry.createdAt), 'dd MMM yyyy HH:mm')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {entry.target?.email || entry.target?.name || '—'}
                        {entry.target?.name && <span className="text-gray-400 ml-1">({entry.target.name})</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {entry.admin ? (entry.admin.name || entry.admin.email) : <span className="text-gray-300">{t('admin.system')}</span>}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {entry.reason || <span className="text-gray-300">—</span>}
                        {entry.allowReply && <span className="ml-2 text-xs text-gray-400">({t('admin.replyLinkSent')})</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet: Cards */}
          <div className="lg:hidden space-y-3">
            {entries.map((entry) => {
              const badge = ACTION_LABELS[entry.action] ?? { label: entry.action, color: 'bg-gray-100 text-gray-700' };
              return (
                <div key={entry.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-gray-400">
                      {format(new Date(entry.createdAt), 'dd MMM yyyy HH:mm')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-900">
                    {entry.target?.email || entry.target?.name || '—'}
                    {entry.target?.name && <span className="text-gray-400 ml-1">({entry.target.name})</span>}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('admin.admin_role')}: {entry.admin ? (entry.admin.name || entry.admin.email) : <span className="text-gray-400">{t('admin.system')}</span>}
                  </div>
                  {entry.reason && (
                    <div className="text-xs text-gray-500 break-words">
                      {entry.reason}
                      {entry.allowReply && <span className="ml-1 text-gray-400">({t('admin.replyLinkSent')})</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </>
      )}
    </div>
  );
};

export default AuditLogTab;
